import { defineStore } from 'pinia'
const axios = require('axios');
import { Settings as LuxonSettings } from "luxon";

let userDataInitializedResolver = null;

export const useUser = defineStore('user', {
    state: () => {
        return {
            id: null,
            masqueraded: null,
            name: null,
            email: null,
            company: {},
            permissions: [],
            language_id: null,
            locale: null,
            timezone: null,
            canChangePassword: false,
            otherNetworks: [],
            userDataInitialized: new Promise((resolve) => (userDataInitializedResolver = resolve)),
            temporaryPassword: false,
            draftPostsCount: 0,
            actionablePostsCount: 0,
            assistantsEnabled: false,
            assistant: null
        }
    },

    getters: {
        isAuthenticated(state) {
            return state.id !== null;
        },

        canAccessAdmin(state) {
            return state.permissions && state.permissions.length > 0;
        },

        currentCompany(state) {
            return state.company;
        },

        language(state) {
            return state.language_id;
        },

        hasTemporaryPassword(state) {
            return state.temporaryPassword;
        }
    },

    actions: {
        login(payload) {
            return axios
                .get('/sanctum/csrf-cookie')
                .then(() => {
                    return axios.post('/api/me', payload);
                })
                .then(response => {
                    this.setUserData(response.data.data);
                    userDataInitializedResolver();

                    if(response.data.data.needs_onboarding === true) {
                        window.location = '/onboarding';
                        return;
                    }

                    // Redirect intended
                    const intendedRoute = localStorage.getItem('intendedRoute');
                    if(intendedRoute) {
                        localStorage.removeItem('intendedRoute');
                        window.location = intendedRoute;
                        return;
                    }


                    window.location = '/dashboard';
                })
                .catch(error => {
                    if (error.response !== undefined) {
                        return error.response.data.message;
                    } else {
                        return error;
                    }
                });
        },

        recoverPassword(payload) {
            return axios.post('/api/recover-password', payload)
                .then((response) => {
                    //
                })
                .catch(error => {
                    if (error.response !== undefined) {
                        return error.response.data.message;
                    } else {
                        return error;
                    }
                });
        },

        resetPassword(payload) {
            return axios.post('/api/reset-password', payload)
                .then((response) => {
                    //
                })
                .catch(error => {
                    if (error.response !== undefined) {
                        return error.response.data.message;
                    } else {
                        return error;
                    }
                });
        },

        logout() {
            axios.delete('/api/me').then(response => {
                this.$reset();
                this.shutdownIntercom();

                window.location = '/login';
            })
        },

        session() {
            return axios.get('/api/me').then(response => {
                if (response.data !== undefined && response.data.data !== undefined) {
                    this.setUserData(response.data.data);
                    userDataInitializedResolver();
                    this.bootIntercom(response.data.data.intercom);
                } else {
                    this.logout();
                }
            }).catch(error => {
                console.log("Err: ", error);
            });
        },

        bootIntercom(intercomData) {
            if(window['Intercom']) {
                intercomData['user_hash'] = intercomData['web_user_hash'];
                delete intercomData['web_user_hash'];
                delete intercomData['ios_user_hash'];
                delete intercomData['android_user_hash'];
                window.Intercom('boot', intercomData);
            } else {
                console.warn('Intercom not configured');
            }
        },

        shutdownIntercom() {
            if (window['Intercom']) {
                window.Intercom('shutdown');
            }
        },

        update(data) {
            let payload = {};
            if (data.timezone !== undefined) {
                this.timezone = data.timezone;
                payload.timezone = data.timezone;
            }

            if (data.language_id !== undefined) {
                this.language_id = data.language_id;
                payload.language_id = data.language_id;
            }

            return axios.put('/api/users/' + this.id, payload)
                .then((response) => {
                    //
                })
                .catch(error => {
                    if (error.response !== undefined && error.response.data.errors !== undefined) {
                        return error.response.data.errors;
                    } else if(error.response !== undefined && error.response.data.message !== undefined) {
                        return error.response.data.message;
                    } else {
                        return error;
                    }
                });
        },

        setUserData(userData) {
            this.id = userData.id;
            this.name = userData.name;
            this.email = userData.email;
            this.permissions = userData.permissions;
            this.company = userData.company;
            this.language_id = userData.language_id ?? 'en';
            this.timezone = userData.timezone ?? 'America/New_York';
            this.locale = userData.locale ?? 'en';
            this.canChangePassword = userData.can_change_password;
            this.otherNetworks = userData.other_networks;
            this.temporaryPassword = userData.temporary_password;
            this.needsOnboarding = userData.needs_onboarding;
            this.draftPostsCount = userData.draft_posts_count;
            this.actionablePostsCount = userData.actionable_posts_count;
            this.assistantsEnabled = userData.assistants_enabled;
            this.assistant = userData.assistant;

            // Luxon defaults for DateTime formatting
            LuxonSettings.defaultLocale = this.locale;
            LuxonSettings.defaultZone = this.timezone;
        },

        toggleAssistants() {
            return axios.post('/api/toggle-assistants')
                .then((response) => {
                    this.assistantsEnabled = response.data;
                    return true;
                })
                .catch(error => {
                    this.assistantsEnabled = !this.assistantsEnabled;
                    throw error;
                });
        }
    }
})
